import React from 'react';
import {
  IonContent,
  IonGrid,
  IonButton,
  IonRow,
  IonCol,
  IonCard,
  IonCardContent,
  IonRouterLink,
} from '@ionic/react';
import {
  FormControlLabel,
  RadioGroup,
  Stack,
  Typography,
  Radio,
  TextField,
  MenuItem,
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import useFetch from 'use-http';
import PropTypes from 'prop-types';
import { TaupeTextField } from 'components/StyledInput';
import PageWithBackButton from 'components/generic/PageWithBackButton';
import appStrings from 'common/app_strings';
import appColors from 'common/app_colors';
import { goalTypeButtons } from './utils';
import './GoalsCreate.css';

const { goals: { goalsEdit: STRINGS, goalsCommon: COMMON_STRINGS }, common } = appStrings;

const GoalsEdit = ({ history, location }) => {
  const {
    control,
    handleSubmit,
    formState: { isDirty, isValid },
  } = useForm({ mode: 'onChange' });
  const { patch } = useFetch();
  const onSubmit = async (data) => {
    await patch(`/goals/${location?.state?.currentGoal?.id}`, data);
    const pushTo = location?.state?.from || '/tabs/goals';
    history.push(pushTo);
  };

  const goalTypeButton = goalTypeButtons.find((button) => button.key === location?.state?.currentGoal?.type);

  return (
    <PageWithBackButton backButtonText="Edit Goal">
      <IonContent fullscreen>
        <form>
          <IonGrid>
            <IonRow>
              <IonCol>
                <Stack spacing={2} mb={5}>
                  <button
                    type="button"
                    className={`goals-type-button goals-type-${location?.state?.currentGoal?.type}`}
                    style={{
                      backgroundColor: goalTypeButton?.bgColor || appColors.pills.orange2,
                    }}
                  >
                    <img
                      src={goalTypeButton?.icon || '/assets/goals/bullseye_outline.svg'}
                      className="goals-type-button-icon"
                      alt={`${location?.state?.currentGoal?.type} goal icon`}
                    />
                    <Typography
                      variant="h4"
                      fontWeight={700}
                      color="#fff"
                      fontSize={24}
                      width="100%"
                      textTransform="capitalize"
                    >
                      {location?.state?.currentGoal?.type}
                    </Typography>
                  </button>
                </Stack>
              </IonCol>
            </IonRow>
            <div className="goals-smart-wrapper">
              <Typography
                variant="h4"
                fontWeight={700}
                fontSize={20}
                sx={{ textAlign: 'center', mr: 1, mb: 1.5 }}
              >
                {STRINGS.title}
              </Typography>
              <IonRouterLink routerLink="/goals-create/smart">
                <div className="goals-smart-link">?</div>
              </IonRouterLink>
            </div>
            <IonRow>
              <IonCol>
                <IonCard
                  color="light"
                  style={{ marginLeft: 0, marginRight: 0, marginBottom: 0 }}
                >
                  <IonCardContent>
                    <Controller
                      control={control}
                      name="content"
                      defaultValue={location?.state?.currentGoal?.content}
                      rules={{ required: true }}
                      render={({ field }) => (
                        <TextField
                          // eslint-disable-next-line react/jsx-props-no-spreading
                          {...field}
                          variant="standard"
                          multiline
                          minRows={3}
                          maxRows={5}
                          fullWidth
                          inputProps={{
                            maxLength: 200,
                            autoCapitalize: 'on',
                            inputMode: 'text',
                          }}
                          // eslint-disable-next-line react/jsx-no-duplicate-props
                          InputProps={{ disableUnderline: true }}
                        />
                      )}
                    />
                  </IonCardContent>
                </IonCard>
              </IonCol>
            </IonRow>
            <IonRow>
              <IonCol>
                <IonCard
                  color="light"
                  style={{ marginLeft: 0, marginRight: 0, marginBottom: 0 }}
                >
                  <IonCardContent>
                    <Typography
                      variant="h2"
                      color="#000"
                      fontWeight="500 !important"
                      mb="12px !important"
                    >
                      {COMMON_STRINGS.shareWith}
                    </Typography>
                    <Controller
                      control={control}
                      name="visibility"
                      defaultValue={
                        location?.state?.currentGoal?.visibility
                      }
                      rules={{ required: true }}
                      render={({ field }) => (
                        // eslint-disable-next-line react/jsx-props-no-spreading
                        <RadioGroup {...field}>
                          <FormControlLabel
                            value="team"
                            control={(
                              <Radio
                                sx={{
                                  '&, &.Mui-checked': {
                                    color: '#063D8F',
                                  },
                                }}
                              />
                            )}
                            label={COMMON_STRINGS.myPanel}
                          />
                          <FormControlLabel
                            value="public"
                            control={(
                              <Radio
                                sx={{
                                  '&, &.Mui-checked': {
                                    color: '#063D8F',
                                  },
                                }}
                              />
                            )}
                            label={COMMON_STRINGS.public}
                          />
                        </RadioGroup>
                      )}
                    />
                  </IonCardContent>
                </IonCard>
              </IonCol>
            </IonRow>
            <IonRow>
              <IonCol>
                <IonCard
                  color="light"
                  style={{ marginLeft: 0, marginRight: 0 }}
                >
                  <IonCardContent>
                    <Typography
                      variant="h2"
                      color="#000"
                      fontWeight="500 !important"
                      mb="12px !important"
                    >
                      {COMMON_STRINGS.goalProgress}
                    </Typography>
                    <Controller
                      control={control}
                      name="status"
                      defaultValue={location?.state?.currentGoal?.status}
                      rules={{ required: true }}
                      render={({ field }) => (
                        <TaupeTextField
                          // eslint-disable-next-line react/jsx-props-no-spreading
                          {...field}
                          select
                          size="small"
                          fullWidth
                        >
                          <MenuItem value="in_progress">{COMMON_STRINGS.inProgress}</MenuItem>
                          <MenuItem value="completed">{COMMON_STRINGS.completed}</MenuItem>
                        </TaupeTextField>
                      )}
                    />
                  </IonCardContent>
                </IonCard>
              </IonCol>
            </IonRow>
            <IonRow>
              <IonCol>
                <IonButton
                  size="large"
                  expand="block"
                  shape="round"
                  onClick={handleSubmit(onSubmit)}
                  disabled={!isDirty || !isValid}
                >
                  {common.submit}
                </IonButton>
              </IonCol>
            </IonRow>
          </IonGrid>
        </form>
      </IonContent>
    </PageWithBackButton>
  );
};

GoalsEdit.propTypes = {
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }).isRequired,
  location: PropTypes.oneOfType([PropTypes.any]).isRequired,
};

export default GoalsEdit;
